/**
 * Recovery from a dead sign-in grant.
 *
 * When the refresh token has expired, B2C refuses to renew the tokens with `invalid_grant` ("AADB2C90080: The provided grant has expired").
 * msal-browser keeps that refresh token in its cache (only a `bad_token` sub-error makes it drop one) and the cache lives in localStorage, so it
 * outlives the browser session: every silent renewal fails with the same 400 and the user is never asked to sign in again.
 * The way out is to forget the cached sign-in and start a clean one, once: if a clean sign-in fails too, the error is shown instead of looping.
 */

/** sessionStorage key holding when the last recovery started. It survives the sign-in redirect, which is what makes "once" hold. */
export const RECOVERY_STORAGE_KEY = "OPER8_SIGNIN_RECOVERY_AT";

/** A second recovery inside this window means the first one did not help. */
export const RECOVERY_WINDOW_MS = 5 * 60 * 1000;

/**
 * msal-browser error codes meaning no cached token can be renewed silently: only a new sign-in helps.
 * `monitor_window_timeout` is what a dead refresh token ends in: msal-browser then tries the renewal in a hidden iframe, which times out.
 */
const DEAD_GRANT_ERROR_CODES = ["invalid_grant", "interaction_required", "login_required", "consent_required", "bad_token", "no_tokens_found", "monitor_window_timeout"];

/** B2C's own code for an expired grant, found in the message of the `invalid_grant` error. */
const GRANT_EXPIRED_B2C_CODE = "AADB2C90080";

interface IAuthErrorLike {
    errorCode?: string;
    errorMessage?: string;
    message?: string;
    name?: string;
}

type RecoveryStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

let recoveredWithoutStorage = false;

/** True for the errors that mean the cached grant is dead, as opposed to a network failure or a bug. */
export const isDeadGrantError = (error: unknown): boolean => {
    const authError = error as IAuthErrorLike | null | undefined;
    if (!authError || typeof authError !== "object") return false;
    if (authError.name === "InteractionRequiredAuthError") return true;
    if (authError.errorCode && DEAD_GRANT_ERROR_CODES.includes(authError.errorCode)) return true;
    return (authError.errorMessage ?? authError.message ?? "").includes(GRANT_EXPIRED_B2C_CODE);
};

/**
 * Says whether a recovery may start now, and records that it did. False when one started less than `RECOVERY_WINDOW_MS` ago.
 * With storage blocked the memory is lost with the page, so a recovery is allowed once per page load.
 */
export const beginRecovery = (storage: RecoveryStorage, now: number): boolean => {
    try {
        const last = Number(storage.getItem(RECOVERY_STORAGE_KEY));
        if (last > 0 && now - last < RECOVERY_WINDOW_MS) return false;

        storage.setItem(RECOVERY_STORAGE_KEY, String(now));
        return true;
    } catch {
        if (recoveredWithoutStorage) return false;

        recoveredWithoutStorage = true;
        return true;
    }
};

/** A renewal worked: the last recovery did its job, so the next dead grant may start a new one. */
export const endRecovery = (storage: RecoveryStorage): void => {
    try {
        storage.removeItem(RECOVERY_STORAGE_KEY);
    } catch {
        // Storage blocked: nothing was recorded.
    }
};
